/**
 * User registration handler
 */
import { pushFunnelEvent } from '@utils/dataLayer';
import BaseHandler from './BaseHandler';
import DOMUtils from '../utils/DOMUtils';
import FormValidator from '../utils/FormValidator';
import {
  FORM_SELECTORS, FIELD_SELECTORS, MESSAGE_SELECTORS, STEP_SELECTORS,
} from '../constants';

class RegisterHandler extends BaseHandler {
  // Field mapping for registration form
  static FIELD_MAP = {
    first_name: FIELD_SELECTORS.regFirstName,
    last_name: FIELD_SELECTORS.regLastName,
    salutation: FIELD_SELECTORS.regSalutation,
    country: FIELD_SELECTORS.regCountry,
    zip_code: FIELD_SELECTORS.regZipCode,
    birthday: FIELD_SELECTORS.regBirthday,
  };

  constructor(form, api, stepNavigator, messages) {
    super(form, api, stepNavigator, messages);
    this.validator = new FormValidator(messages);
    this.bindPasswordMismatchValidation();
  }

  bindPasswordMismatchValidation() {
    const passwordInput = this.form.querySelector(FIELD_SELECTORS.regPassword);
    const passwordConfirmInput = this.form.querySelector(FIELD_SELECTORS.regPasswordConfirm);

    if (!passwordInput || !passwordConfirmInput) {
      return;
    }

    const validateConfirm = () => {
      const password = passwordInput.value;
      const passwordConfirm = passwordConfirmInput.value;

      if (password && !passwordConfirm) {
        DOMUtils.showError(
          passwordConfirmInput,
          this.validator.createError('error_field_required'),
          false,
          false,
        );
        return;
      }

      if (!password && passwordConfirm) {
        DOMUtils.showError(
          passwordConfirmInput,
          this.messages.getMessage('error_password_match'),
          false,
          false,
        );
        return;
      }

      if (
        password
        && passwordConfirm
        && !FormValidator.validatePasswordsMatch(password, passwordConfirm)
      ) {
        DOMUtils.showError(
          passwordConfirmInput,
          this.messages.getMessage('error_password_match'),
          false,
          false,
        );
        return;
      }

      DOMUtils.clearError(passwordConfirmInput);
    };

    const validatePassword = () => {
      const password = passwordInput.value;

      const passwordError = this.validator.validatePassword(password);
      if (passwordError) {
        DOMUtils.showError(passwordInput, passwordError, false, false);
      } else {
        DOMUtils.clearError(passwordInput);
      }

      validateConfirm();
    };

    passwordInput.addEventListener('input', validatePassword);
    passwordConfirmInput.addEventListener('input', validateConfirm);
  }

  /**
   * Handle registration
   */
  async handleRegister(email, isSSOForm = false) {
    // Check if this is SSO registration
    const ssoToken = sessionStorage.getItem('azds_sso_token');
    const isSSORegistration = !!ssoToken;

    // Use appropriate field selectors based on form type
    const fieldSelectors = isSSOForm ? {
      first_name: FIELD_SELECTORS.regSsoFirstName,
      last_name: FIELD_SELECTORS.regSsoLastName,
      salutation: FIELD_SELECTORS.regSsoSalutation,
      country: FIELD_SELECTORS.regSsoCountry,
      zip_code: FIELD_SELECTORS.regSsoZipCode,
      birthday: FIELD_SELECTORS.regSsoBirthday,
    } : {
      first_name: FIELD_SELECTORS.regFirstName,
      last_name: FIELD_SELECTORS.regLastName,
      salutation: FIELD_SELECTORS.regSalutation,
      country: FIELD_SELECTORS.regCountry,
      zip_code: FIELD_SELECTORS.regZipCode,
      birthday: FIELD_SELECTORS.regBirthday,
    };

    const passwordInput = this.form.querySelector(FIELD_SELECTORS.regPassword);
    const passwordConfirmInput = this.form.querySelector(FIELD_SELECTORS.regPasswordConfirm);

    const formData = {
      email,
      ...DOMUtils.getFieldValues(fieldSelectors, this.form),
    };

    const decanterParam = this.form.getAttribute('data-decanter-param');
    if (decanterParam) {
      formData.decanter = decanterParam;
    }

    // Add password fields only for regular registration
    if (!isSSORegistration) {
      formData.password = passwordInput.value;
      formData.password_confirm = passwordConfirmInput.value;
    }

    // Validate form (skip password validation for SSO)
    const errors = this.validator.validateRegisterForm(formData, isSSORegistration);
    if (errors) {
      // Show all errors on all fields
      Object.entries(errors).forEach(([fieldKey, error]) => {
        if (fieldKey === 'password') {
          DOMUtils.showError(passwordInput, error);
        } else if (fieldKey === 'password_confirm') {
          DOMUtils.showError(passwordConfirmInput, error);
        } else {
          // Try to find field in current form (regular or SSO)
          const fieldSelector = this.getFieldSelector(fieldKey, isSSOForm);
          const input = this.form.querySelector(fieldSelector);
          if (input) {
            // Use error message from validator (already translated via messages)
            DOMUtils.showError(input, error);
          }
        }
      });
      return;
    }

    // Use appropriate API endpoint
    const apiCall = isSSORegistration
      ? () => this.api.ssoCompleteRegistration(ssoToken, formData)
      : () => {
        formData.auth_page_url = DOMUtils.getCurrentPageUrl();
        return this.api.register(formData);
      };

    const formSelector = isSSOForm ? FORM_SELECTORS.registerSso : FORM_SELECTORS.register;
    const method = isSSORegistration ? 'Google' : 'Email';

    await this.handleApiCall(
      formSelector,
      apiCall,
      {
        successCallback: (data) => {
          // Clean up SSO data
          if (isSSORegistration) {
            sessionStorage.removeItem('azds_sso_token');
          }

          if (data.needs_verification) {
            this.showVerificationStep(data.email);
          } else {
            // Track Sign Up success for Google (step 3)
            pushFunnelEvent({
              section: 'Sign Up',
              step: 3,
              label: 'Sign Up - Success',
              method: 'Google',
            });

            const redirectUrl = this.getRedirectUrl();
            localStorage.removeItem('azds_sso_redirect');
            window.location.href = redirectUrl || data.redirect || window.location.href;
          }
        },
        errorCallback: (data) => {
          this.handleRegisterError(data, passwordInput, isSSOForm, method);
        },
      },
    );
  }

  /**
   * Handle registration errors
   */
  handleRegisterError(data, passwordInput, isSSOForm = false, method = 'Email') {
    let errorMessage = this.messages.getMessageFromApi(data.message, {});

    // Normalize error code (handle both with and without 'error_' prefix)
    const errorCode = data.code && !data.code.startsWith('error_')
      ? `error_${data.code}`
      : (data.code || '');

    if (!errorMessage && errorCode) {
      errorMessage = this.messages.hasMessage(errorCode)
        ? this.messages.getMessage(errorCode)
        : this.messages.getMessage('error_generic');
    } else if (!errorMessage) {
      errorMessage = this.messages.getMessage('error_generic');
    }

    // Track Sign Up error (step 2)
    pushFunnelEvent({
      section: 'Sign Up',
      step: 2,
      label: 'Sign Up - Error',
      method,
      errorMessage,
    });

    // General errors that should be shown in message element (not on specific fields)
    if (errorCode === 'error_auth_token_failed') {
      const messageSelector = isSSOForm
        ? MESSAGE_SELECTORS.registerSsoMessage : MESSAGE_SELECTORS.registerMessage;
      const stepSelector = isSSOForm ? STEP_SELECTORS.registerSso : STEP_SELECTORS.register;

      // Search in step first (even if hidden), then in form, then in document
      const stepElement = this.form.querySelector(stepSelector);
      const messageElement = stepElement?.querySelector(messageSelector)
        || this.form.querySelector(messageSelector)
        || document.querySelector(messageSelector)
        || this.form.querySelector('.auth-form__message, .form__message');

      if (messageElement) {
        DOMUtils.showMessage(messageElement, errorMessage, 'error');
        return;
      }
    }

    if (data.code === 'weak_password' || data.code === 'password_mismatch' || data.code === 'password_min' || data.code === 'password_confirm') {
      if (passwordInput) {
        DOMUtils.showError(passwordInput, errorMessage);
      }
    } else if (data.code === 'invalid_email' || data.code === 'email_exists' || data.code === 'email') {
      const emailInput = this.form.querySelector(
        isSSOForm ? FIELD_SELECTORS.regSsoEmail : FIELD_SELECTORS.regEmail,
      ) || this.form.querySelector(FIELD_SELECTORS.userEmail);
      if (emailInput) {
        DOMUtils.showError(emailInput, errorMessage);
      }
    } else {
      // For other errors, try to find the first visible input field
      const firstInput = this.form.querySelector('input[type="text"], input[type="email"], select');
      if (firstInput) {
        DOMUtils.showError(firstInput, errorMessage);
      }
    }
  }

  /**
   * Show verification step
   */
  showVerificationStep(email) {
    DOMUtils.setFieldValue(FIELD_SELECTORS.verifyEmailDisplay, email, this.form);
    this.stepNavigator.goToStep('verify-email');

    // Track Sign Up step 3: Verification
    pushFunnelEvent({
      section: 'Sign Up',
      step: 3,
      label: 'Sign Up - Verification',
      method: 'Email',
    });
  }

  /**
   * Get field selector by name
   */
  getFieldSelector(fieldName, isSSOForm = false) {
    if (isSSOForm) {
      const ssoFieldMap = {
        first_name: FIELD_SELECTORS.regSsoFirstName,
        last_name: FIELD_SELECTORS.regSsoLastName,
        salutation: FIELD_SELECTORS.regSsoSalutation,
        country: FIELD_SELECTORS.regSsoCountry,
        zip_code: FIELD_SELECTORS.regSsoZipCode,
        birthday: FIELD_SELECTORS.regSsoBirthday,
      };
      return ssoFieldMap[fieldName] || null;
    }
    return this.constructor.FIELD_MAP[fieldName] || null;
  }
}

export default RegisterHandler;
